/**
 * Phase 9.18b: Enhanced Add Item Modal
 * Phase 9.34: Added quantity/rate support for invoice line items
 *
 * Features:
 * - Partial billing support (editable amount, default to remaining)
 * - Already-invoiced info for each item
 * - "Already on this invoice" section
 * - "Fully Invoiced" collapsed accordion
 * - Create new billable item option
 * - Optional quantity × rate pricing for display purposes
 */
import { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { ChevronRight, Plus, Check } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { invalidateProjectListQueries } from '@/lib/cache-utils';
import { getSuggestedTaxRate } from '@/lib/taxRates';
import { useProjectById } from '@/hooks';
import { getCategoryDisabledReason, CATEGORY_OPTIONS } from '@/components/billable-items/CategorySelect';
import type { BillableItem } from '@/lib/schemas';
import { deriveAmountFromQtyRate } from '@/lib/qtyRateAmount';
import { lowerOutcome, type LowerChoice } from '@/lib/lowerItemTotal';
import { LowerItemTotalChoice } from './LowerItemTotalChoice';

interface AddItemModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoiceId: string;
  projectId: string;
  existingItemIds: string[]; // IDs of billable items already on this invoice
  onSuccess: () => void;
}

/**
 * The categories the "Create New Billable Item" sub-form offers, in the order
 * CATEGORY_OPTIONS declares them. Renewal and internal_work are deliberately
 * absent here (renewal items are created on the project page; internal_work is
 * a deprecated $0-only category) — this list preserves exactly what the form
 * offered before QA round 2 pulled its labels from the shared option list.
 */
const CREATABLE_CATEGORIES = [
  'professional_fee',
  'add_service',
  'billable_expense',
  'pass_through',
] as const;

interface SelectedItem {
  billableItemId: string;
  amount: number;
  taxRate: number;
  // R4 (SPEC-v017-completion-2026-09-27): the category this LINE is billed
  // under. Starts at the billable item's own category and is editable per
  // line — it drives the suggested tax rate and is never written back to
  // the stored billable item.
  category: BillableItem['category'];
  // Phase 9.34: Optional quantity/rate for display
  showHourlyRate: boolean;
  quantity: string;
  unitPrice: string;
  unitType: '' | 'hours' | 'days' | 'units' | 'each';
  // Christa 2026-09-25 (feedback 9725dd36): when the amount is below what remains on the item, she says whether the
  // item is now that amount or the rest stays to bill later. Nothing preselected; Add waits for the answer.
  lowerChoice?: LowerChoice | null;
}

/** True when the line bills less than what remains on the item, so the item-total question applies. */
function billsLessThanRemaining(amount: number, remaining: number): boolean {
  return amount > 0 && amount < remaining - 0.005;
}

/**
 * R3 AC1: how long after an add settles the invoice detail is read again in the background. The created rows are
 * written straight into the cache (a read right after a write can come from a Cloud Run instance that has not seen it
 * yet), so the screen is right at once; this later read brings the per-tax detail the create answer does not carry and
 * confirms the lines, without making her wait or reload.
 */
const DETAIL_REFRESH_DELAY_MS = 1500;

export function AddItemModal({
  open,
  onOpenChange,
  invoiceId,
  projectId,
  existingItemIds,
  onSuccess,
}: AddItemModalProps) {
  const queryClient = useQueryClient();
  const [selectedItems, setSelectedItems] = useState<Map<string, SelectedItem>>(new Map());
  const [showFullyInvoiced, setShowFullyInvoiced] = useState(false);
  const [showCreateNew, setShowCreateNew] = useState(false);
  const [newItemDescription, setNewItemDescription] = useState('');
  const [newItemAmount, setNewItemAmount] = useState(''); // Billable item total amount
  const [newItemCategory, setNewItemCategory] = useState<'professional_fee' | 'add_service' | 'billable_expense' | 'pass_through'>('professional_fee');
  // Advanced options state
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [newItemInvoiceAmount, setNewItemInvoiceAmount] = useState(''); // Amount to invoice (can be partial)
  const [newItemTaxRate, setNewItemTaxRate] = useState('');
  // Phase 9.34: Quantity/rate fields for display
  const [showHourlyRate, setShowHourlyRate] = useState(false);
  const [newItemQuantity, setNewItemQuantity] = useState('');
  const [newItemUnitPrice, setNewItemUnitPrice] = useState('');
  const [newItemUnitType, setNewItemUnitType] = useState<'hours' | 'days' | 'units' | 'each' | ''>('');

  // Fetch all billable items for this project
  const { data: billableItemsData, isLoading } = useQuery({
    queryKey: ['billable-items', projectId],
    queryFn: async () => {
      return api.billableItems.list({ project_id: projectId });
    },
    enabled: open && !!projectId,
  });

  // R2 (SPEC-bc-pst-tax-engine-2026-08-04, AC10): the suggested per-line rate
  // derives from the TENANT'S CONFIGURED rate (invoice_defaults.tax_rate),
  // not a module constant — same source BillingSessionPage reads.
  const { data: invoiceDefaults } = useQuery({
    queryKey: ['settings', 'invoice-defaults'],
    queryFn: async () => {
      try {
        return await api.settings.getInvoiceDefaults();
      } catch {
        return null; // fall back to the module default rate
      }
    },
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });
  const tenantTaxRate = invoiceDefaults?.tax_rate ?? null;

  // fix/v017-addmodal-profee: the project's workflow_stage (and is_template)
  // drive the same Professional-Fee-vs-Add-Service restriction the project
  // page's CategorySelect already enforces (Phase 7.5.3 / ebd232ac). A single
  // invoice can only ever hold items from ONE project (backend
  // _assert_project_matches in models/invoice_items.py hard-blocks a
  // mismatch), so the invoice's own projectId is the only project this modal
  // ever needs.
  const { data: projectData } = useProjectById(projectId);
  const workflowStage = projectData?.project?.workflow_stage ?? undefined;
  const isTemplate = projectData?.project?.is_template ?? undefined;
  const professionalFeeDisabledReason = getCategoryDisabledReason(
    'professional_fee',
    workflowStage,
    isTemplate
  );

  // Tracks whether the user has EVER changed the category select themselves.
  // Before that, the "Create New" default may need to self-correct once the
  // project's workflow_stage arrives (it can resolve after this modal has
  // already rendered with the pre-restriction default). Once touched, the
  // user's explicit choice is never overwritten out from under them.
  const categoryTouched = useRef(false);

  // Categorize items
  const { availableItems, onThisInvoice, fullyInvoiced } = useMemo(() => {
    const allItems = billableItemsData?.billable_items || [];

    // Items already on this invoice
    const onInvoice = allItems.filter((item: BillableItem) =>
      existingItemIds.includes(item.id)
    );

    // Fully invoiced (not on this invoice, remaining = 0)
    const fullyInvoicedItems = allItems.filter((item: BillableItem) =>
      !existingItemIds.includes(item.id) &&
      !item.cancelled &&
      (item.remaining_amount ?? item.amount) <= 0
    );

    // Available to add (not on this invoice, has remaining amount)
    const available = allItems.filter((item: BillableItem) =>
      !existingItemIds.includes(item.id) &&
      !item.cancelled &&
      (item.remaining_amount ?? item.amount) > 0
    );

    return {
      availableItems: available,
      onThisInvoice: onInvoice,
      fullyInvoiced: fullyInvoicedItems,
    };
  }, [billableItemsData, existingItemIds]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!open) {
      setSelectedItems(new Map());
      setShowFullyInvoiced(false);
      setShowCreateNew(false);
      setNewItemDescription('');
      setNewItemAmount('');
      setShowAdvancedOptions(false);
      setNewItemInvoiceAmount('');
      setNewItemTaxRate('');
      // Phase 9.34: Reset quantity/rate fields
      setShowHourlyRate(false);
      setNewItemQuantity('');
      setNewItemUnitPrice('');
      setNewItemUnitType('');
      categoryTouched.current = false;
    }
  }, [open]);

  // fix/v017-addmodal-profee: correct the "Create New" default the moment we
  // learn the project can't take a new Professional Fee (same rule as
  // CategorySelect's getCategoryDisabledReason) — but only while the user
  // hasn't made their own choice yet, so a workflow_stage fetch that resolves
  // after the create form is already open never clobbers a deliberate pick.
  useEffect(() => {
    if (!categoryTouched.current && professionalFeeDisabledReason && newItemCategory === 'professional_fee') {
      setNewItemCategory('add_service');
    }
  }, [professionalFeeDisabledReason, newItemCategory]);

  // Suggested tax rate now comes from the shared @/lib/taxRates module
  // (v0.14.4 R1) — no local copy of the category→rate mapping here.

  // Toggle item selection
  const toggleItem = (item: BillableItem) => {
    const newMap = new Map(selectedItems);
    if (newMap.has(item.id)) {
      newMap.delete(item.id);
    } else {
      const remaining = item.remaining_amount ?? item.amount;
      newMap.set(item.id, {
        billableItemId: item.id,
        amount: remaining,
        category: item.category,
        taxRate: getSuggestedTaxRate(item.category, tenantTaxRate),
        // Phase 9.34: Initialize qty/rate as empty
        showHourlyRate: false,
        quantity: '',
        unitPrice: '',
        unitType: '',
      });
    }
    setSelectedItems(newMap);
  };

  const updateItemLowerChoice = (itemId: string, lowerChoice: LowerChoice) => {
    const newMap = new Map(selectedItems);
    const existing = newMap.get(itemId);
    if (existing) {
      newMap.set(itemId, { ...existing, lowerChoice });
      setSelectedItems(newMap);
    }
  };

  // Update amount for selected item
  const updateItemAmount = (itemId: string, amount: number) => {
    const newMap = new Map(selectedItems);
    const existing = newMap.get(itemId);
    if (existing) {
      newMap.set(itemId, { ...existing, amount });
      setSelectedItems(newMap);
    }
  };

  // R4 (SPEC-v017-completion-2026-09-27, AC1/AC2): change the category this
  // LINE is billed under. The suggested tax rate is recomputed from the new
  // category immediately, so a switch to billable_expense or pass_through
  // drops the line's tax to 0 in the UI before submit. This is her explicit
  // choice for THIS invoice line only — it never writes back to the stored
  // billable item's own category (AC4) and never fires on its own (AC5).
  const updateItemCategory = (
    itemId: string,
    category: BillableItem['category']
  ) => {
    const newMap = new Map(selectedItems);
    const existing = newMap.get(itemId);
    if (existing) {
      newMap.set(itemId, {
        ...existing,
        category,
        taxRate: getSuggestedTaxRate(category, tenantTaxRate),
      });
      setSelectedItems(newMap);
    }
  };

  // Phase 9.34: Update qty/rate for selected item
  // R1 (SPEC-v017-completion-2026-09-27, AC1): changing quantity or unit
  // price recomputes the amount that gets billed. This only fires from a
  // change to quantity/unitPrice themselves -- a direct edit to the amount
  // field (updateItemAmount, above) is an explicit override and is never
  // silently clobbered until quantity or unit price change again.
  const updateItemQtyRate = (
    itemId: string,
    field: 'quantity' | 'unitPrice' | 'unitType',
    value: string
  ) => {
    const newMap = new Map(selectedItems);
    const existing = newMap.get(itemId);
    if (existing) {
      const updated = { ...existing, [field]: value };
      if (field === 'quantity' || field === 'unitPrice') {
        const derived = deriveAmountFromQtyRate(updated.quantity, updated.unitPrice);
        if (derived !== null) {
          updated.amount = derived;
        }
      }
      newMap.set(itemId, updated);
      setSelectedItems(newMap);
    }
  };

  const refreshDetailInBackground = () => {
    window.setTimeout(() => {
      void queryClient.invalidateQueries({ queryKey: ['invoices', invoiceId, 'full'], exact: true });
    }, DETAIL_REFRESH_DELAY_MS);
  };

  // Add selected items mutation
  const addItemsMutation = useMutation({
    mutationFn: async () => {
      const itemsToAdd = Array.from(selectedItems.values());
      const results = [];
      // Lines whose item total could not be lowered after the add. The line itself is saved, so the modal still
      // closes and refreshes (a retry would add the line twice); she is told the total was not lowered.
      let lowerFailures = 0;

      for (const selectedItem of itemsToAdd) {
        const billableItem = availableItems.find(
          (item: BillableItem) => item.id === selectedItem.billableItemId
        );
        if (!billableItem) continue;

        const taxAmount = (selectedItem.amount * selectedItem.taxRate) / 100;

        // Phase 9.34: Parse qty/rate if provided
        const quantity = selectedItem.quantity ? parseFloat(selectedItem.quantity) : null;
        const unitPrice = selectedItem.unitPrice ? parseFloat(selectedItem.unitPrice) : null;
        const unitType = selectedItem.unitType || null;

        const result = await api.invoiceItems.create({
          invoice_id: invoiceId,
          billable_item_id: selectedItem.billableItemId,
          project_id: projectId,
          description: billableItem.description,
          amount: selectedItem.amount,
          tax_rate: selectedItem.taxRate,
          tax_amount: taxAmount,
          // QA round 2 (christa-reviewer HIGH 1): the category she chose for
          // THIS line, not just the tax rate it implies. Without it the line
          // has no category of its own and the rendered PDF classifies it
          // from the stored billable item — printing an app fee she billed as
          // an expense inside the professional-services table. Never written
          // back to the billable item (R4 AC4).
          category: selectedItem.category,
          // Phase 9.34: Optional qty/rate for display
          quantity,
          unit_price: unitPrice,
          unit_type: unitType,
        });
        const remaining = billableItem.remaining_amount ?? billableItem.amount;
        const created = (result as { invoice_item?: { id?: string } }).invoice_item;
        if (
          selectedItem.lowerChoice === 'lower' &&
          billsLessThanRemaining(selectedItem.amount, remaining) &&
          created?.id
        ) {
          // "The item is now $X": the server lowers the item to this line plus what other invoices bill, in one
          // transaction, never below them (same path as the line editor).
          try {
            const lowered = await api.invoiceItems.update(created.id, {
              amount: selectedItem.amount,
              lower_billable_item_total: true,
            });
            const total = (lowered as { invoice_item?: { billable_item_amount?: number | null } })
              .invoice_item?.billable_item_amount;
            // Use the lowered total right away so the new row never shows the old one while the page refreshes.
            if (total != null) {
              results.push({
                ...result,
                invoice_item: { ...result.invoice_item, billable_item_amount: Number(total) },
              });
              continue;
            }
          } catch {
            lowerFailures += 1;
          }
        }
        results.push(result);
      }

      if (lowerFailures > 0) {
        toast.error(
          lowerFailures === 1
            ? 'Line added. The item total was not lowered.'
            : 'Lines added. Some item totals were not lowered.'
        );
      }
      return results;
    },
    onSuccess: (results) => {
      // Update the detail cache directly with the created items from response data.
      // Cloud Run multi-instance architecture means GET-after-POST can return stale
      // data from a different instance. Using setQueryData avoids this.
      const createdItems = results.map((r: any) => r.invoice_item);
      // A lowered item total applies to every line of that item already on this invoice too.
      const loweredTotals = new Map<string, number>();
      for (const created of createdItems) {
        if (created?.billable_item_id && typeof created.billable_item_amount === 'number') {
          loweredTotals.set(created.billable_item_id, created.billable_item_amount);
        }
      }

      queryClient.setQueryData(['invoices', invoiceId, 'full'], (old: any) => {
        if (!old) return old;
        const existing = (old.invoice_items || []).map((i: { billable_item_id?: string | null }) => {
          const lowered = i.billable_item_id ? loweredTotals.get(i.billable_item_id) : undefined;
          return lowered !== undefined ? { ...i, billable_item_amount: lowered } : i;
        });
        const newItems = [...existing, ...createdItems];
        const feeAmount = newItems.reduce((sum: number, i: any) => sum + (i.amount || 0), 0);
        const taxAmount = newItems.reduce((sum: number, i: any) => sum + (i.tax_amount || 0), 0);
        return {
          ...old,
          invoice_items: newItems,
          invoice: {
            ...old.invoice,
            fee_amount: feeAmount,
            tax_amount: taxAmount,
            total_amount: feeAmount + taxAmount,
          },
        };
      });

      // Also update invoice list cache so navigating back shows correct totals
      queryClient.setQueriesData(
        {
          predicate: (query) => {
            const key = query.queryKey;
            if (key[0] !== 'invoices' || key.length < 2) return false;
            const secondKey = key[1];
            if (typeof secondKey === 'string' && secondKey.includes('-')) return false;
            if (secondKey === 'next-number') return false;
            return true;
          }
        },
        (old: any) => {
          if (!old?.invoices) return old;
          const addedFee = createdItems.reduce((sum: number, i: any) => sum + (i.amount || 0), 0);
          const addedTax = createdItems.reduce((sum: number, i: any) => sum + (i.tax_amount || 0), 0);
          return {
            ...old,
            invoices: old.invoices.map((inv: any) =>
              inv.id === invoiceId
                ? {
                    ...inv,
                    fee_amount: (inv.fee_amount || 0) + addedFee,
                    tax_amount: (inv.tax_amount || 0) + addedTax,
                    total_amount: (inv.total_amount || 0) + addedFee + addedTax,
                  }
                : inv
            ),
          };
        }
      );

      // Background invalidations for eventual consistency — but EXCLUDE the detail
      // query we just updated via setQueryData. The broad ['invoices'] prefix match
      // would trigger a refetch that returns stale data from Cloud Run and overwrites
      // our optimistic cache update.
      queryClient.invalidateQueries({
        queryKey: ['invoices'],
        predicate: (query) => {
          // Skip the detail query — we already set it directly
          const key = query.queryKey;
          return !(key.length === 3 && key[2] === 'full');
        },
      });
      queryClient.invalidateQueries({ queryKey: ['invoice-items'] });
      queryClient.invalidateQueries({ queryKey: ['billable-items', projectId] });
      invalidateProjectListQueries(queryClient);
      refreshDetailInBackground();

      setSelectedItems(new Map());
      onOpenChange(false);
      onSuccess();
    },
    // Theme 5 (mutation reliability): the bulk add had NO onError, so a failed
    // create (validation, 4xx, network drop) was completely silent — the modal
    // stayed open with no feedback and the user assumed it worked. The loop in
    // mutationFn is not transactional (each item is its own POST), so a mid-loop
    // failure can leave SOME items added; surface the failure and let the
    // onSuccess invalidations (which won't run on error) be replaced by an
    // explicit refresh so partially-added items still appear.
    onError: (err: any) => {
      // Refresh so any items that DID get created before the failure are visible.
      queryClient.invalidateQueries({ queryKey: ['invoices', invoiceId, 'full'] });
      queryClient.invalidateQueries({ queryKey: ['invoice-items'] });
      toast.error(err?.message || 'Failed to add items. Please try again.');
    },
  });

  // Create new billable item mutation
  const createItemMutation = useMutation({
    mutationFn: async () => {
      const billableAmount = parseFloat(newItemAmount);
      if (isNaN(billableAmount) || billableAmount <= 0) {
        throw new Error('Amount must be a positive number');
      }

      // Determine invoice amount (use advanced option if set, otherwise same as billable)
      const invoiceAmount = showAdvancedOptions && newItemInvoiceAmount
        ? parseFloat(newItemInvoiceAmount)
        : billableAmount;

      if (isNaN(invoiceAmount) || invoiceAmount <= 0) {
        throw new Error('Invoice amount must be a positive number');
      }

      if (invoiceAmount > billableAmount) {
        throw new Error('Invoice amount cannot exceed billable amount');
      }

      // Determine tax rate (use advanced option if set, otherwise calculate from category)
      const taxRate = showAdvancedOptions && newItemTaxRate !== ''
        ? parseFloat(newItemTaxRate)
        : getSuggestedTaxRate(newItemCategory, tenantTaxRate);

      if (isNaN(taxRate) || taxRate < 0) {
        throw new Error('Tax rate must be a non-negative number');
      }

      // Create the billable item with full billable amount
      const billableItem = await api.billableItems.create({
        project_id: projectId,
        description: newItemDescription,
        amount: billableAmount,
        category: newItemCategory,
        item_status: 'confirmed',
      });

      // Add to invoice with potentially partial amount
      const taxAmount = (invoiceAmount * taxRate) / 100;

      // Phase 9.34: Parse quantity/rate fields if provided
      const quantity = newItemQuantity ? parseFloat(newItemQuantity) : null;
      const unitPrice = newItemUnitPrice ? parseFloat(newItemUnitPrice) : null;
      const unitType = newItemUnitType || null;

      const invoiceItemResult = await api.invoiceItems.create({
        invoice_id: invoiceId,
        billable_item_id: billableItem.billable_item.id,
        project_id: projectId,
        description: newItemDescription,
        amount: invoiceAmount,
        tax_rate: taxRate,
        tax_amount: taxAmount,
        // Phase 9.34: Optional quantity/rate for display
        quantity,
        unit_price: unitPrice,
        unit_type: unitType,
      });

      return { billableItem, invoiceItem: invoiceItemResult.invoice_item };
    },
    onSuccess: ({ invoiceItem }) => {
      // Update the detail cache directly with the created item from response data.
      // Cloud Run multi-instance architecture means GET-after-POST can return stale
      // data from a different instance. Using setQueryData avoids this.
      queryClient.setQueryData(['invoices', invoiceId, 'full'], (old: any) => {
        if (!old) return old;
        const newItems = [...(old.invoice_items || []), invoiceItem];
        const feeAmount = newItems.reduce((sum: number, i: any) => sum + (i.amount || 0), 0);
        const taxAmount = newItems.reduce((sum: number, i: any) => sum + (i.tax_amount || 0), 0);
        return {
          ...old,
          invoice_items: newItems,
          invoice: {
            ...old.invoice,
            fee_amount: feeAmount,
            tax_amount: taxAmount,
            total_amount: feeAmount + taxAmount,
          },
        };
      });

      // Also update invoice list cache so navigating back shows correct totals
      queryClient.setQueriesData(
        {
          predicate: (query) => {
            const key = query.queryKey;
            if (key[0] !== 'invoices' || key.length < 2) return false;
            const secondKey = key[1];
            if (typeof secondKey === 'string' && secondKey.includes('-')) return false;
            if (secondKey === 'next-number') return false;
            return true;
          }
        },
        (old: any) => {
          if (!old?.invoices) return old;
          const addedFee = invoiceItem.amount || 0;
          const addedTax = invoiceItem.tax_amount || 0;
          return {
            ...old,
            invoices: old.invoices.map((inv: any) =>
              inv.id === invoiceId
                ? {
                    ...inv,
                    fee_amount: (inv.fee_amount || 0) + addedFee,
                    tax_amount: (inv.tax_amount || 0) + addedTax,
                    total_amount: (inv.total_amount || 0) + addedFee + addedTax,
                  }
                : inv
            ),
          };
        }
      );

      // Background invalidations — exclude detail query (already set via setQueryData)
      queryClient.invalidateQueries({
        queryKey: ['invoices'],
        predicate: (query) => !(query.queryKey.length === 3 && query.queryKey[2] === 'full'),
      });
      queryClient.invalidateQueries({ queryKey: ['invoice-items'] });
      queryClient.invalidateQueries({ queryKey: ['billable-items', projectId] });
      invalidateProjectListQueries(queryClient);
      refreshDetailInBackground();

      setShowCreateNew(false);
      setNewItemDescription('');
      setNewItemAmount('');
      setShowAdvancedOptions(false);
      setNewItemInvoiceAmount('');
      setNewItemTaxRate('');
      // Phase 9.34: Reset quantity/rate fields
      setNewItemQuantity('');
      setNewItemUnitPrice('');
      setNewItemUnitType('');
      onOpenChange(false);
      onSuccess();
    },
  });

  // Add waits until every line that bills less than its item's remainder has an answer.
  const lowerChoiceMissing = Array.from(selectedItems.values()).some((selected) => {
    const billableItem = availableItems.find((item: BillableItem) => item.id === selected.billableItemId);
    if (!billableItem) return false;
    const remaining = billableItem.remaining_amount ?? billableItem.amount;
    return billsLessThanRemaining(selected.amount, remaining) && !selected.lowerChoice;
  });

  const handleAddSelected = () => {
    if (selectedItems.size > 0 && !lowerChoiceMissing) {
      addItemsMutation.mutate();
    }
  };

  // R1 (SPEC-v017-completion-2026-09-27, AC1): quantity/rate drive the
  // amount that gets billed on the "Create New" form, same as
  // updateItemQtyRate does for an existing item above. Changing either
  // input recomputes newItemAmount; a direct edit to the Amount field
  // itself is never touched by these two handlers, so it stays an explicit,
  // never-clobbered override.
  const handleNewItemQuantityChange = (value: string) => {
    setNewItemQuantity(value);
    const derived = deriveAmountFromQtyRate(value, newItemUnitPrice);
    if (derived !== null) {
      setNewItemAmount(derived.toFixed(2));
    }
  };

  const handleNewItemUnitPriceChange = (value: string) => {
    setNewItemUnitPrice(value);
    const derived = deriveAmountFromQtyRate(newItemQuantity, value);
    if (derived !== null) {
      setNewItemAmount(derived.toFixed(2));
    }
  };

  const handleCreateNew = () => {
    if (newItemDescription && newItemAmount) {
      createItemMutation.mutate();
    }
  };

  const handleCancel = () => {
    setSelectedItems(new Map());
    setShowCreateNew(false);
    onOpenChange(false);
  };

  const formatCurrency = (amount: number) => {
    return amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Items to Invoice</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading billable items...</p>
          ) : (
            <>
              {/* Available Billable Items */}
              {availableItems.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">
                    Available Billable Items
                  </h3>
                  <div className="space-y-3">
                    {availableItems.map((item: BillableItem) => {
                      const isSelected = selectedItems.has(item.id);
                      const selectedData = selectedItems.get(item.id);
                      const remaining = item.remaining_amount ?? item.amount;
                      const invoiced = item.invoiced_amount ?? 0;

                      return (
                        <div
                          key={item.id}
                          className={`rounded-lg border p-3 transition-colors ${
                            isSelected ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <Checkbox
                              id={`item-${item.id}`}
                              checked={isSelected}
                              onCheckedChange={() => toggleItem(item)}
                              className="mt-1"
                              data-testid={`select-item-${item.id}`}
                            />
                            <div className="flex-1 min-w-0">
                              <label
                                htmlFor={`item-${item.id}`}
                                className="text-sm font-medium cursor-pointer block"
                              >
                                {item.description}
                              </label>
                              <div className="text-xs text-muted-foreground mt-1 space-y-1">
                                <div className="flex justify-between">
                                  <span>Billable total:</span>
                                  <span className="font-medium">${formatCurrency(item.amount)}</span>
                                </div>
                                {invoiced > 0 && (
                                  <div className="flex justify-between text-amber-600">
                                    <span>Already invoiced:</span>
                                    <span>${formatCurrency(invoiced)}</span>
                                  </div>
                                )}
                                <div className="flex justify-between">
                                  <span>Remaining:</span>
                                  <span className="font-medium">${formatCurrency(remaining)}</span>
                                </div>
                              </div>

                              {/* Editable amount when selected */}
                              {isSelected && selectedData && (
                                <div className="mt-3 pt-3 border-t space-y-2">
                                  <div className="flex items-center gap-2">
                                    <Label htmlFor={`amount-${item.id}`} className="text-xs whitespace-nowrap">
                                      Bill this invoice:
                                    </Label>
                                    <div className="flex items-center">
                                      <span className="text-sm text-muted-foreground mr-1">$</span>
                                      <Input
                                        id={`amount-${item.id}`}
                                        type="number"
                                        min="0.01"
                                        step="0.01"
                                        max={remaining}
                                        value={selectedData.amount}
                                        onChange={(e) =>
                                          updateItemAmount(item.id, parseFloat(e.target.value) || 0)
                                        }
                                        className="h-8 w-28"
                                        data-testid={`amount-input-${item.id}`}
                                      />
                                    </div>
                                  </div>
                                  {selectedData.amount > remaining && (
                                    <p className="text-xs text-destructive mt-1">
                                      Amount exceeds remaining ({formatCurrency(remaining)})
                                    </p>
                                  )}
                                  {billsLessThanRemaining(selectedData.amount, remaining) && (
                                    <LowerItemTotalChoice
                                      lineAmount={selectedData.amount}
                                      outcome={lowerOutcome(selectedData.amount, item.amount, item.amount - remaining)}
                                      value={selectedData.lowerChoice ?? null}
                                      onChange={(choice) => updateItemLowerChoice(item.id, choice)}
                                      testIdPrefix={`lower-item-total-${item.id}`}
                                    />
                                  )}
                                  {/* R4 (SPEC-v017-completion-2026-09-27, AC1-AC3):
                                      the category this LINE is billed under, with
                                      the tax consequence shown at the point of
                                      choice. Starts at the billable item's own
                                      category; changing it recomputes the line's
                                      tax rate immediately. This never rewrites
                                      the stored billable item (AC4) and never
                                      changes on its own (AC5). */}
                                  <div className="flex items-center gap-2 flex-wrap mt-2">
                                    <Label htmlFor={`category-${item.id}`} className="text-xs whitespace-nowrap">
                                      Category for this line:
                                    </Label>
                                    <select
                                      id={`category-${item.id}`}
                                      value={selectedData.category}
                                      onChange={(e) =>
                                        updateItemCategory(item.id, e.target.value as BillableItem['category'])
                                      }
                                      className="h-8 text-xs rounded-md border border-input bg-background px-2"
                                      data-testid={`category-select-${item.id}`}
                                    >
                                      {/* QA round 2 (christa-reviewer HIGH 3):
                                          every option says what it MEANS and
                                          what it does to tax, in the app's own
                                          words (CATEGORY_OPTIONS, the same list
                                          the project page's CategorySelect
                                          renders). Five bare labels were how
                                          the wrong 2026-09-24 email advice --
                                          Pass-Through for an application fee --
                                          went uncontradicted on the page.
                                          internal_work is offered ONLY when the
                                          stored item already is one, so the
                                          select's value is always a valid
                                          option without advertising a $0-only
                                          category. */}
                                      {CATEGORY_OPTIONS
                                        .filter(option => option.value !== 'internal_work'
                                          || item.category === 'internal_work')
                                        .map(option => (
                                          <option key={option.value} value={option.value}>
                                            {option.label} — {option.description} ({option.taxInfo})
                                          </option>
                                        ))}
                                    </select>
                                    {(() => {
                                      // AC3: what this category means for tax on
                                      // THIS line, live against the current amount.
                                      // formatCurrency (not toFixed) so a
                                      // four-figure tax reads $1,250.00 like
                                      // every other number in this modal.
                                      const rate = getSuggestedTaxRate(selectedData.category, tenantTaxRate);
                                      const taxAmount = (selectedData.amount * rate) / 100;
                                      const taxLabel = invoiceDefaults?.tax_label ?? 'GST';
                                      return (
                                        <span
                                          className="text-xs text-muted-foreground"
                                          data-testid={`category-tax-hint-${item.id}`}
                                        >
                                          {rate > 0
                                            ? `+${rate}% ${taxLabel} ($${formatCurrency(taxAmount)})`
                                            : `no tax ($${formatCurrency(taxAmount)})`}
                                        </span>
                                      );
                                    })()}
                                  </div>
                                  {/* A closed native select shows only the
                                      selected label, so the meaning of the
                                      category she is CURRENTLY on has to be on
                                      the page as well as inside the dropdown. */}
                                  <p
                                    className="text-xs text-muted-foreground mt-1"
                                    data-testid={`category-help-${item.id}`}
                                  >
                                    {CATEGORY_OPTIONS.find(
                                      option => option.value === selectedData.category
                                    )?.description}
                                  </p>
                                  {/* Phase 9.34: Hourly rate item checkbox */}
                                  <div className="flex items-center space-x-2 mt-2">
                                    <Checkbox
                                      id={`hourly-${item.id}`}
                                      checked={selectedData.showHourlyRate}
                                      onCheckedChange={(checked) => {
                                        const newMap = new Map(selectedItems);
                                        newMap.set(item.id, { ...selectedData, showHourlyRate: checked === true });
                                        setSelectedItems(newMap);
                                      }}
                                    />
                                    <Label
                                      htmlFor={`hourly-${item.id}`}
                                      className="text-xs text-muted-foreground cursor-pointer"
                                    >
                                      Hourly rate item (show qty × rate on PDF)
                                    </Label>
                                  </div>
                                  {selectedData.showHourlyRate && (
                                    <div className="text-xs text-muted-foreground mt-2 pl-6">
                                      <div className="flex items-center gap-1">
                                        <Input
                                          type="number"
                                          min="0.0001"
                                          step="0.0001"
                                          placeholder="Qty"
                                          value={selectedData.quantity}
                                          onChange={(e) => updateItemQtyRate(item.id, 'quantity', e.target.value)}
                                          className="h-7 w-14 text-xs"
                                          data-testid={`item-quantity-${item.id}`}
                                        />
                                        <span>×</span>
                                        <span>$</span>
                                        <Input
                                          type="number"
                                          min="0"
                                          step="0.01"
                                          placeholder="Rate"
                                          value={selectedData.unitPrice}
                                          onChange={(e) => updateItemQtyRate(item.id, 'unitPrice', e.target.value)}
                                          className="h-7 w-16 text-xs"
                                          data-testid={`item-unit-price-${item.id}`}
                                        />
                                        <select
                                          value={selectedData.unitType}
                                          onChange={(e) => updateItemQtyRate(item.id, 'unitType', e.target.value)}
                                          className="h-7 text-xs rounded-md border border-input bg-background px-1"
                                        >
                                          <option value="">-</option>
                                          <option value="hours">hrs</option>
                                          <option value="days">days</option>
                                          <option value="units">units</option>
                                          <option value="each">each</option>
                                        </select>
                                      </div>
                                      {selectedData.quantity && selectedData.unitPrice && (() => {
                                        const calc = parseFloat(selectedData.quantity) * parseFloat(selectedData.unitPrice);
                                        const mismatch = Math.abs(calc - selectedData.amount) > 0.01;
                                        if (!mismatch) {
                                          return <p className="mt-1">= ${calc.toFixed(2)}</p>;
                                        }
                                        // R1 (SPEC-v017-completion-2026-09-27, AC2): the old
                                        // discount-excuse copy granted permission for a silent
                                        // default instead of naming what would actually be
                                        // billed. Amount is now derived by default (above), so
                                        // a mismatch here only
                                        // happens after a deliberate hand-edit to the amount
                                        // field -- name both numbers and say which one bills.
                                        return (
                                          <p className="mt-1 text-amber-600">
                                            Billing ${selectedData.amount.toFixed(2)}. Quantity x rate would be ${calc.toFixed(2)} - this is an explicit override, not the default.
                                          </p>
                                        );
                                      })()}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* No available items message */}
              {availableItems.length === 0 && fullyInvoiced.length === 0 && onThisInvoice.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No billable items for this project yet.
                </p>
              )}

              {/* Already on this invoice */}
              {onThisInvoice.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">
                    Already on this Invoice
                  </h3>
                  <div className="space-y-2">
                    {onThisInvoice.map((item: BillableItem) => (
                      <div
                        key={item.id}
                        className="flex items-center gap-2 text-sm text-muted-foreground py-2"
                      >
                        <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                        <span className="truncate">{item.description}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Fully Invoiced (collapsed) */}
              {fullyInvoiced.length > 0 && (
                <Collapsible open={showFullyInvoiced} onOpenChange={setShowFullyInvoiced}>
                  <CollapsibleTrigger className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
                    <ChevronRight
                      className={`h-4 w-4 transition-transform ${showFullyInvoiced ? 'rotate-90' : ''}`}
                    />
                    Fully Invoiced ({fullyInvoiced.length})
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-2 pl-6 space-y-2">
                    {fullyInvoiced.map((item: BillableItem) => (
                      <div key={item.id} className="text-sm text-muted-foreground py-1">
                        {item.description} — ${formatCurrency(item.amount)}
                      </div>
                    ))}
                  </CollapsibleContent>
                </Collapsible>
              )}

              {/* Divider */}
              <div className="border-t" />

              {/* Create New Billable Item */}
              {showCreateNew ? (
                <div className="space-y-3">
                  <h3 className="text-sm font-medium">Create New Billable Item</h3>
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="new-description" className="text-xs">
                        Description
                      </Label>
                      <Input
                        id="new-description"
                        value={newItemDescription}
                        onChange={(e) => setNewItemDescription(e.target.value)}
                        placeholder="Enter description..."
                        className="mt-1"
                        data-testid="new-item-description"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="new-amount" className="text-xs">
                          {showAdvancedOptions ? 'Billable Amount' : 'Amount'}
                        </Label>
                        <div className="flex items-center mt-1">
                          <span className="text-sm text-muted-foreground mr-1">$</span>
                          <Input
                            id="new-amount"
                            type="number"
                            min="0.01"
                            step="0.01"
                            value={newItemAmount}
                            onChange={(e) => setNewItemAmount(e.target.value)}
                            placeholder="0.00"
                            data-testid="new-item-amount"
                          />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="new-category" className="text-xs">
                          Category
                        </Label>
                        <select
                          id="new-category"
                          value={newItemCategory}
                          onChange={(e) => {
                            categoryTouched.current = true;
                            setNewItemCategory(e.target.value as typeof newItemCategory);
                          }}
                          className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                          data-testid="new-item-category"
                        >
                          {/* QA round 2 (christa-reviewer HIGH 3 + naming):
                              same names and same guidance as the per-line
                              control and the project page, from the one
                              exported list. The four categories offered here
                              are unchanged -- renewal and internal_work are
                              still not creatable from this form. */}
                          {CATEGORY_OPTIONS
                            .filter(option => (CREATABLE_CATEGORIES as readonly string[]).includes(option.value))
                            .map(option => (
                              <option
                                key={option.value}
                                value={option.value}
                                disabled={option.value === 'professional_fee'
                                  && !!professionalFeeDisabledReason}
                              >
                                {option.label}
                                {option.value === 'professional_fee' && professionalFeeDisabledReason
                                  ? ' (restricted)'
                                  : ''}
                                {' '}— {option.description} ({option.taxInfo})
                              </option>
                            ))}
                        </select>
                        {/* Phase 7.5.3 rule, reused via CategorySelect's getCategoryDisabledReason
                            (fix/v017-addmodal-profee) — same restriction, same explanation text as
                            the project page's Professional Fee restriction. */}
                        {professionalFeeDisabledReason && (
                          <p
                            className="text-xs text-muted-foreground mt-1"
                            data-testid="new-item-category-restriction-note"
                          >
                            {professionalFeeDisabledReason}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Hourly Rate Item Checkbox */}
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="show-hourly-rate"
                        checked={showHourlyRate}
                        onCheckedChange={(checked) => setShowHourlyRate(checked === true)}
                        data-testid="show-hourly-rate"
                      />
                      <Label
                        htmlFor="show-hourly-rate"
                        className="text-xs text-muted-foreground cursor-pointer"
                      >
                        Hourly rate item (show qty × rate on PDF)
                      </Label>
                    </div>

                    {/* Phase 9.34: Quantity/Rate Fields */}
                    {showHourlyRate && (
                      <div className="space-y-3 pl-6">
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <Label htmlFor="new-quantity" className="text-xs">
                              Quantity
                            </Label>
                            <Input
                              id="new-quantity"
                              type="number"
                              min="0.0001"
                              step="0.0001"
                              value={newItemQuantity}
                              onChange={(e) => handleNewItemQuantityChange(e.target.value)}
                              placeholder="e.g., 3"
                              className="mt-1"
                              data-testid="new-item-quantity"
                            />
                          </div>
                          <div>
                            <Label htmlFor="new-unit-price" className="text-xs">
                              Unit Price
                            </Label>
                            <div className="flex items-center mt-1">
                              <span className="text-sm text-muted-foreground mr-1">$</span>
                              <Input
                                id="new-unit-price"
                                type="number"
                                min="0"
                                step="0.01"
                                value={newItemUnitPrice}
                                onChange={(e) => handleNewItemUnitPriceChange(e.target.value)}
                                placeholder="e.g., 250"
                                data-testid="new-item-unit-price"
                              />
                            </div>
                          </div>
                          <div>
                            <Label htmlFor="new-unit-type" className="text-xs">
                              Unit Type
                            </Label>
                            <select
                              id="new-unit-type"
                              value={newItemUnitType}
                              onChange={(e) => setNewItemUnitType(e.target.value as typeof newItemUnitType)}
                              className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                              data-testid="new-item-unit-type"
                            >
                              <option value="">Select...</option>
                              <option value="hours">Hours</option>
                              <option value="days">Days</option>
                              <option value="units">Units</option>
                              <option value="each">Each</option>
                            </select>
                          </div>
                        </div>
                        {newItemQuantity && newItemUnitPrice && (() => {
                          const calculatedAmount = parseFloat(newItemQuantity) * parseFloat(newItemUnitPrice);
                          const targetAmount = newItemInvoiceAmount
                            ? parseFloat(newItemInvoiceAmount)
                            : (newItemAmount ? parseFloat(newItemAmount) : 0);
                          const mismatch = targetAmount > 0 && Math.abs(calculatedAmount - targetAmount) > 0.01;
                          return (
                            <div className="mt-2">
                              <p className={`text-xs ${mismatch ? 'text-amber-600' : 'text-muted-foreground'}`}>
                                Calculated: {newItemQuantity} × ${newItemUnitPrice} = ${calculatedAmount.toFixed(2)}
                                {mismatch && (
                                  // R1 (SPEC-v017-completion-2026-09-27, AC2): the old
                                  // discount-excuse copy granted permission for a silent
                                  // default instead of naming what would actually be
                                  // billed. Amount is now derived by default
                                  // (handleNewItemQuantityChange /
                                  // handleNewItemUnitPriceChange, above), so a mismatch
                                  // here only happens after a deliberate hand-edit to the
                                  // amount field -- name both numbers and say which one bills.
                                  <span className="block mt-1">
                                    Billing ${targetAmount.toFixed(2)}. Quantity x rate would be ${calculatedAmount.toFixed(2)} - this is an explicit override, not the default.
                                  </span>
                                )}
                              </p>
                            </div>
                          );
                        })()}
                      </div>
                    )}

                    {/* Advanced Options Toggle */}
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="show-advanced"
                        checked={showAdvancedOptions}
                        onCheckedChange={(checked) => setShowAdvancedOptions(checked === true)}
                        data-testid="show-advanced-options"
                      />
                      <Label
                        htmlFor="show-advanced"
                        className="text-xs text-muted-foreground cursor-pointer"
                      >
                        Advanced options (partial billing, custom tax rate)
                      </Label>
                    </div>

                    {/* Advanced Options Fields */}
                    {showAdvancedOptions && (
                      <div className="space-y-3 pl-6">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label htmlFor="new-invoice-amount" className="text-xs">
                              Invoice Amount
                            </Label>
                            <div className="flex items-center mt-1">
                              <span className="text-sm text-muted-foreground mr-1">$</span>
                              <Input
                                id="new-invoice-amount"
                                type="number"
                                min="0.01"
                                step="0.01"
                                max={newItemAmount ? parseFloat(newItemAmount) : undefined}
                                value={newItemInvoiceAmount}
                                onChange={(e) => setNewItemInvoiceAmount(e.target.value)}
                                placeholder={newItemAmount || '0.00'}
                                data-testid="new-item-invoice-amount"
                              />
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                              Leave blank to invoice full amount
                            </p>
                          </div>
                          <div>
                            <Label htmlFor="new-tax-rate" className="text-xs">
                              Tax Rate (%)
                            </Label>
                            <Input
                              id="new-tax-rate"
                              type="number"
                              min="0"
                              step="0.01"
                              value={newItemTaxRate}
                              onChange={(e) => setNewItemTaxRate(e.target.value)}
                              placeholder={getSuggestedTaxRate(newItemCategory, tenantTaxRate).toString()}
                              className="mt-1"
                              data-testid="new-item-tax-rate"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                              Leave blank for {getSuggestedTaxRate(newItemCategory, tenantTaxRate)}% (based on category)
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowCreateNew(false)}
                        disabled={createItemMutation.isPending}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleCreateNew}
                        disabled={
                          !newItemDescription ||
                          !newItemAmount ||
                          parseFloat(newItemAmount) <= 0 ||
                          createItemMutation.isPending
                        }
                        data-testid="create-new-item-button"
                      >
                        {createItemMutation.isPending ? 'Creating...' : 'Create & Add'}
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowCreateNew(true)}
                  className="text-muted-foreground hover:text-foreground"
                  data-testid="show-create-new-button"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create New Billable Item
                </Button>
              )}

              {/* Error messages */}
              {addItemsMutation.isError && (
                <p className="text-sm text-destructive">
                  {addItemsMutation.error instanceof Error
                    ? addItemsMutation.error.message
                    : 'Failed to add items'}
                </p>
              )}
              {createItemMutation.isError && (
                <p className="text-sm text-destructive">
                  {createItemMutation.error instanceof Error
                    ? createItemMutation.error.message
                    : 'Failed to create item'}
                </p>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} disabled={addItemsMutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleAddSelected}
            disabled={selectedItems.size === 0 || addItemsMutation.isPending || lowerChoiceMissing}
            data-testid="add-selected-button"
          >
            {addItemsMutation.isPending
              ? 'Adding...'
              : `Add Selected (${selectedItems.size})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
